import { useCallback } from 'react';

import { when } from 'mobx';

import { auth } from '../../services/auth';
import { useStores } from '../../stores/useStores';

import { useDirectRegCountrySelection, useNewDirectRegSignUp } from './useNewLanderSignUp';
import { useOpenAuthWizard } from './useOpenAuthWizard';

// handleVerificationCompletion is not awaited and its backend login can be slow, so give sign in plenty of time.
const USER_DATA_READY_TIMEOUT_MS = 30_000;

/**
 * Opens the welcome bonus after a direct_reg sign up.
 * - direct_reg flag on: the redesigned country select (when sign_up_flow_country_selection is on) followed by
 *   the redesigned bonus. The player is marked as authenticated once the last step closes.
 * - direct_reg flag off: the legacy bonus, which closes through `legacyBeforeClose` when one is given.
 */
export function useOpenDirectRegWelcomeBonus() {
  const { authStore } = useStores();
  const { openAuthWizard } = useOpenAuthWizard();
  const isNewDirectReg = useNewDirectRegSignUp();
  const isSignUpCountrySelectionEnabled = useDirectRegCountrySelection();

  return useCallback(
    (legacyBeforeClose?: VoidFunction) => {
      if (!isNewDirectReg) {
        openAuthWizard('WelcomeBonusDialog', {
          persistent: true,
          ...(legacyBeforeClose && { beforeClose: legacyBeforeClose }),
        });
        return;
      }

      const completeAuth = () => {
        // Skipped when the player signed out while a step was open (X on the country select signs out).
        if (authStore.userDataReady) {
          authStore.setAuthCompleted();
        }
        authStore.setIsAuthWizardOpen(false);
      };

      const openBonus = (beforeClose: VoidFunction) =>
        openAuthWizard('WelcomeBonusRedesign', { persistent: true, beforeClose });

      const openBonusAndComplete = () =>
        openBonus(() => {
          auth.isAgeConfirmed = false;
          completeAuth();
        });

      // Sign in can still be running, and a quick Claim on the bonus would find the player not authorized yet. Wait
      // for it; if the player signed out or sign in failed, neither the country select nor the bonus could do
      // anything and would only trap them in a persistent portal.
      const openFirstStep = () =>
        isSignUpCountrySelectionEnabled
          ? openAuthWizard('ChooseCountryRedesign', {
              persistent: true,
              beforeClose: () => {
                // X on the country select signs the player out before closing: land on the landing page, no bonus.
                if (!auth.userId) {
                  auth.isAgeConfirmed = false;
                  authStore.setIsAuthWizardOpen(false);
                  return;
                }

                // Lets the country portal finish closing before the bonus takes its place.
                setTimeout(() => {
                  // The player can sign out during the delay.
                  if (!auth.userId) {
                    auth.isAgeConfirmed = false;
                    authStore.setIsAuthWizardOpen(false);
                    return;
                  }

                  openBonusAndComplete();
                }, 200);
              },
            })
          : openBonusAndComplete();

      // A third party sign up is signed in already: open right away instead of a tick later.
      if (authStore.userDataReady) {
        openFirstStep();
        return;
      }

      when(() => authStore.userDataReady, { timeout: USER_DATA_READY_TIMEOUT_MS }).then(openFirstStep, () => {
        authStore.setIsAuthWizardOpen(false);

        // Sign in is just slow: the steps are skipped, but the player must not stay unauthorized until a reload.
        void when(() => authStore.userDataReady).then(() => authStore.setAuthCompleted());
      });
    },
    [authStore, isNewDirectReg, isSignUpCountrySelectionEnabled, openAuthWizard],
  );
}
